import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  BellRinging,
  CheckCircle,
  ForkKnife,
  PencilSimple,
  PlayCircle,
  Ruler,
  Target,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import {
  useGetReportsAwaitingReviewQuery,
  useGetMeasurementsDueQuery,
  useSendMeasurementReminderMutation,
} from "../../store/services/dailyReportsApi";
import {
  useGetClientsWithoutGoalsQuery,
  useUpdatePatientMutation,
} from "../../store/services/patientsApi";
import HoverTip from "../ui/HoverTip";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectIsAdmin,
} from "../../store/slices/authSlice";
import { useToast } from "../../contexts/ToastContext";
import EmptyDraw from "../ui/EmptyDraw";
import { formatHebrewDate } from "../../utils/dateUtils";
import ContentLoader from "../shared/ContentLoader";
import "./NutritionPendingSection.css";

/**
 * The nutrition clinic's "פעולות ממתינות".
 *
 * A nutrition coach has no appointment-approval / session-summary / payment
 * loop, so for these clinics the three-stage appointment queue is replaced
 * wholesale by the two things that actually wait on them:
 *
 *   1. eating reports that closed and nobody has reviewed
 *   2. clients whose measurement date has passed
 *   3. clients created without daily goals (cleared the moment goals are saved,
 *      or dismissed for good with the ✕ on the card — Round 3 / Phase 23)
 *
 * Therapy clinics never render this component and never fire its queries —
 * CollapsibleSections branches on selectIsNutritionClinic before either.
 */
function NutritionPendingSection({ onStartDemo = null }) {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const isAdmin = useSelector(selectIsAdmin);

  const [remindedIds, setRemindedIds] = useState([]);
  // Hidden immediately on ✕; the refetch after the write keeps them hidden.
  const [dismissedIds, setDismissedIds] = useState([]);
  const [updatePatient] = useUpdatePatientMutation();

  const { data: reports = [], isLoading: isLoadingReports } =
    useGetReportsAwaitingReviewQuery(
      { clinicId, therapistId, isAdmin },
      { skip: !clinicId || !therapistId }
    );

  const { data: dueClients = [], isLoading: isLoadingDue } =
    useGetMeasurementsDueQuery(
      { clinicId, therapistId, isAdmin },
      { skip: !clinicId || !therapistId }
    );

  const { data: noGoalsClients = [], isLoading: isLoadingNoGoals } =
    useGetClientsWithoutGoalsQuery(
      { clinicId, therapistId, isAdmin },
      { skip: !clinicId || !therapistId }
    );

  const [sendReminder, { isLoading: isSending }] =
    useSendMeasurementReminderMutation();

  const visibleNoGoals = noGoalsClients.filter((p) => !dismissedIds.includes(p.id));

  const isLoading = isLoadingReports || isLoadingDue || isLoadingNoGoals;
  const isEmpty =
    reports.length === 0 && dueClients.length === 0 && visibleNoGoals.length === 0;

  /**
   * Permanent dismissal of the "no goals" reminder for one client. Stored on
   * the patient doc (top-level, so the nutrition map is never overwritten);
   * getClientsWithoutGoals and the workspace banner both honor it.
   */
  const handleDismissGoals = async (event, patient) => {
    event.stopPropagation();
    setDismissedIds((prev) => [...prev, patient.id]);
    try {
      await updatePatient({
        clinicId,
        patientId: patient.id,
        patientData: { nutritionGoalsPromptDismissedAt: new Date().toISOString() },
        therapistId,
        therapistName,
      }).unwrap();
    } catch (error) {
      console.error("[CK-10241] Error dismissing the no-goals reminder:", {
        errorCode: "CK-10241",
        errorMessage: error?.message,
        clinicId,
        patientId: patient.id,
        timestamp: new Date().toISOString(),
      });
      setDismissedIds((prev) => prev.filter((id) => id !== patient.id));
      showError("ההסתרה נכשלה, נסו שוב");
    }
  };

  // The coaching tools live on מרכז הליווי, not the profile
  const openPatient = (patientId, tab) =>
    navigate(`/dashboard/nutrition/${patientId}?tab=${tab}`);

  const handleRemind = async (patient) => {
    try {
      await sendReminder({ clinicId, patientId: patient.id }).unwrap();
      // Track locally too: the query refetch is what really disables the
      // button, but the coach should see it happen immediately.
      setRemindedIds((prev) => [...prev, patient.id]);
      showSuccess("נשלחה תזכורת ללקוח/ה");
    } catch (error) {
      console.error("[CK-10240] Error sending a measurement reminder:", {
        errorCode: "CK-10240",
        errorMessage: error?.message,
        clinicId,
        patientId: patient.id,
        timestamp: new Date().toISOString(),
      });

      const message = String(error?.message || "");
      if (message.includes("ALREADY_NOTIFIED")) {
        showError("כבר נשלחה תזכורת למחזור הנוכחי");
        setRemindedIds((prev) => [...prev, patient.id]);
      } else if (message.includes("NO_ACTIVE_LINK")) {
        showError("אין ללקוח/ה עמוד אישי פעיל. הקישור נמצא בכרטיס הלקוח/ה ובמרכז הליווי");
      } else if (message.includes("SMS_QUOTA_EXCEEDED")) {
        showError("מכסת ה-SMS החודשית נוצלה במלואה");
      } else if (message.includes("PATIENT_HAS_NO_PHONE")) {
        showError("לא נמצא מספר טלפון ללקוח/ה");
      } else {
        showError("שליחת התזכורת נכשלה");
      }
    }
  };

  if (isLoading && isEmpty) {
    return (
      <ContentLoader message="טוען..." />
    );
  }

  if (isEmpty) {
    return (
      <div className="nps-empty">
        <EmptyDraw variant="done" />
        <span>אין פעולות ממתינות</span>
        <span className="nps-empty-subtitle">
          כל דוחות האכילה נסקרו, אין מדידות שממתינות ולכל הלקוחות יש יעדים
        </span>
        {onStartDemo && (
          <button type="button" className="pad-cta" onClick={onStartDemo}>
            <PlayCircle size={16} weight="fill" />
            <span>רוצה לראות איך זה עובד? דוח אכילה לדוגמה</span>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="nps-root">
      {reports.length > 0 && (
        <section className="nps-group">
          <h4 className="nps-group-title">
            <ForkKnife size={16} weight="fill" />
            דוחות אכילה לסקירה
            <span className="nps-count">{reports.length}</span>
          </h4>

          <div className="nps-list" role="list">
            {reports.map((report) => (
              <button
                key={`${report.patientId}-${report.id}`}
                type="button"
                role="listitem"
                className="nps-item"
                onClick={() => openPatient(report.patientId, "dailyReports")}
              >
                <span className="nps-item-main">
                  <span className="nps-item-title">
                    {report.patientName || "לקוח/ה"}
                  </span>
                  <span className="nps-item-sub">
                    {formatHebrewDate(report.date)} ·{" "}
                    {report.totals?.calories ?? 0} קק״ל
                  </span>
                </span>

                {report.hasClientEdits && (
                  <span className="nps-badge nps-badge--edited">
                    <PencilSimple size={12} weight="fill" />
                    הלקוח/ה שינה/תה ערכים
                  </span>
                )}
              </button>
            ))}
          </div>
        </section>
      )}

      {dueClients.length > 0 && (
        <section className="nps-group">
          <h4 className="nps-group-title">
            <Ruler size={16} weight="fill" />
            מדידות שממתינות
            <span className="nps-count">{dueClients.length}</span>
          </h4>

          <div className="nps-list" role="list">
            {dueClients.map((patient) => {
              const schedule = patient.nutrition?.measurementSchedule || {};
              // One reminder per cycle: the server refuses a second one, and
              // the daily job clears lastNotifiedAt when the cycle rolls over.
              const alreadyNotified =
                !!schedule.lastNotifiedAt || remindedIds.includes(patient.id);

              return (
                <div key={patient.id} role="listitem" className="nps-item nps-item--row">
                  <button
                    type="button"
                    className="nps-item-main nps-item-btn"
                    onClick={() => openPatient(patient.id, "measurements")}
                  >
                    <span className="nps-item-title">
                      {[patient.firstName, patient.lastName]
                        .filter(Boolean)
                        .join(" ") || "לקוח/ה"}
                    </span>
                    <span className="nps-item-sub">
                      <WarningCircle size={12} weight="fill" />
                      המועד למדידה עבר
                    </span>
                  </button>

                  <button
                    type="button"
                    className="nps-remind-btn"
                    onClick={() => handleRemind(patient)}
                    disabled={alreadyNotified || isSending}
                    title={
                      alreadyNotified
                        ? "כבר נשלחה תזכורת למחזור הנוכחי"
                        : "שליחת תזכורת ב-SMS"
                    }
                  >
                    {alreadyNotified ? (
                      <>
                        <CheckCircle size={14} weight="fill" />
                        נשלחה תזכורת
                      </>
                    ) : (
                      <>
                        <BellRinging size={14} weight="fill" />
                        שלח תזכורת ללקוח/ה
                      </>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {visibleNoGoals.length > 0 && (
        <section className="nps-group">
          <h4 className="nps-group-title">
            <Target size={16} weight="fill" />
            לקוחות ללא יעדים יומיים
            <span className="nps-count">{visibleNoGoals.length}</span>
          </h4>

          <div className="nps-list" role="list">
            {visibleNoGoals.map((patient) => (
              <div key={patient.id} role="listitem" className="nps-item nps-item--row nps-item--dismissable">
                <button
                  type="button"
                  className="nps-item-main nps-item-btn"
                  onClick={() =>
                    navigate(`/dashboard/nutrition/${patient.id}?tab=menus&setGoals=1`)
                  }
                >
                  <span className="nps-item-title">
                    {[patient.firstName, patient.lastName].filter(Boolean).join(" ") ||
                      "לקוח/ה"}
                  </span>
                  <span className="nps-item-sub">
                    <WarningCircle size={12} weight="fill" />
                    נוצר/ה בלי יעדים — לחיצה פותחת את ההגדרה
                  </span>
                </button>

                <HoverTip text="הסתר — לא להציג שוב עבור לקוח/ה זה/זו">
                  <button
                    type="button"
                    className="nps-dismiss-btn"
                    onClick={(event) => handleDismissGoals(event, patient)}
                    aria-label="הסתרת התזכורת עבור לקוח/ה זה/זו"
                  >
                    <X size={14} weight="bold" />
                  </button>
                </HoverTip>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default NutritionPendingSection;
